import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from 'react-oidc-context';
import {
  Alert,
  AlertActionLink,
  Breadcrumb,
  BreadcrumbItem,
  Button,
  Content,
  EmptyState,
  EmptyStateActions,
  EmptyStateBody,
  EmptyStateFooter,
  Flex,
  FlexItem,
  Icon,
  Modal,
  ModalBody,
  ModalHeader,
  PageBreadcrumb,
  PageSection,
  PageSectionVariants,
  Spinner,
  Wizard,
  WizardStep,
} from '@patternfly/react-core';
import { MicrochipIcon } from '@patternfly/react-icons';
import CenteredSpinner from '@app/CenteredSpinner';
import Unauthenticated from '@app/Unauthenticated';
import { useNotifications } from '../../../../contexts/NotificationContext';
import { useImpersonation } from '../../../../contexts/impersonation/ImpersonationContext';
import { useGpuServerOffer } from '../../../../hooks/useGpuServerOffer';
import { useGpuServerImages } from '../../../../hooks/useGpuServerImages';
import { useGpuServers } from '../../../../hooks/useGpuServers';
import { useStartGpuServer } from '../../../../hooks/useStartGpuServer';
import { usePubkeys } from '../../../../hooks/usePubkeys';
import { useWallet } from '../../../../hooks/useWallet';
import type { GpuServerStartBody } from '../../../../types/models/GpuServer';
import { newIdempotencyKey } from '../../../../utils/idempotencyKey';
import TopUpModal from '../../../Organization/Billing/TopUpModal';
import GpuServerImpersonationAlert from '../GpuServerImpersonationAlert';
import { GPU_SERVER_MAX_ACTIVE } from '../gpuServerConstants';
import { activeServerCount, isShortOf, startReserve } from '../gpuServerBilling';
import ServerStep from './ServerStep';
import LocationStep from './LocationStep';
import ImageStep from './ImageStep';
import KeysStep from './KeysStep';
import ReviewStep, { ReviewFooter } from './ReviewStep';
import type { ReviewError } from './ReviewStep';
import {
  MAX_KEYS,
  MIN_KEYS,
  STEP_IDS,
  initialSession,
  nextSession,
  placeStartError,
  startBlockers,
} from './startGpuServerSteps';
import type { EditableStepId, SubmitSession } from './startGpuServerSteps';

const LIST_PATH = '/datacenter/gpu-servers';

/**
 * Start a GPU server — a five-step wizard (server, location, system image, SSH keys, review) over
 * docs/api-types/gpu-servers.md. The server is paid hour by hour from the prepaid balance only.
 *
 * - The offer is a LIVE read: loaded once when the page opens, never polled (Retry on failure).
 *   While starts are disabled the wizard stays browsable, read-only, with Start disabled.
 * - Every selection is lifted here, above `<Wizard>` (PF unmounts inactive step bodies), so a
 *   background re-load (a hook refetching on an OIDC token renew) never clears a choice.
 * - One idempotency key per wizard session, reused for every submit: a retry after a lost response
 *   replays the first server instead of paying for a second one. An unconfirmed submit locks the
 *   steps until a success; only a `failed` server gets a new key (see `startGpuServerSteps.ts`).
 */
const StartGpuServer: React.FunctionComponent = () => {
  const auth = useAuth();
  const navigate = useNavigate();
  const { addNotification, setDrawerOpen } = useNotifications();
  const { isImpersonating } = useImpersonation();

  // --- Reads (page level, so they survive step navigation) ---
  const { offer, loading: offerLoading, error: offerError, refetch: refetchOffer } = useGpuServerOffer(true);
  const {
    wallet,
    defaultPaymentMethod,
    loading: walletLoading,
    error: walletError,
    refetch: refetchWallet,
  } = useWallet();
  const { servers, error: serversError, refetch: refetchServers } = useGpuServers();
  const { pubkeys, loading: pubkeysLoading, error: pubkeysError, refetch: refetchPubkeys } = usePubkeys();
  const { imagesFor, fetchImages, loadingFor, errorFor } = useGpuServerImages();
  const { start, loading: submitting } = useStartGpuServer();

  // --- Wizard state (lifted above <Wizard>) ---
  const [productId, setProductId] = useState<number | null>(null);
  const [locationCode, setLocationCode] = useState<string | null>(null);
  const [imageCode, setImageCode] = useState<string | null>(null);
  const [keyIds, setKeyIds] = useState<number[]>([]);
  const [acknowledged, setAcknowledged] = useState(false);
  const [session, setSession] = useState<SubmitSession>(() => initialSession(newIdempotencyKey()));
  const [stepErrors, setStepErrors] = useState<Partial<Record<EditableStepId, string>>>({});
  const [reviewError, setReviewError] = useState<ReviewError | null>(null);
  const [lastFailed, setLastFailed] = useState(false);
  const [isTopUpOpen, setIsTopUpOpen] = useState(false);
  const submittingRef = useRef(false);

  const product = useMemo(
    () => (productId !== null ? (offer?.products.find((p) => p.id === productId) ?? null) : null),
    [offer, productId],
  );
  const images = productId !== null ? imagesFor(productId) : null;
  const selectedImage = images?.find((image) => image.code === imageCode) ?? null;
  const usableKeys = useMemo(() => pubkeys.filter((key) => key.id !== null), [pubkeys]);
  const validKeyIds = useMemo(
    () => keyIds.filter((id) => usableKeys.some((key) => key.id === id)),
    [keyIds, usableKeys],
  );
  const selectedKeys = usableKeys.filter((key) => key.id !== null && validKeyIds.includes(key.id));

  // Preselect the product's default image once its list is known, unless a listed one is chosen.
  useEffect(() => {
    if (images === null || images.some((image) => image.code === imageCode)) {
      return;
    }
    setImageCode(images.find((image) => image.is_default)?.code ?? null);
  }, [images, imageCode]);

  const clearStepError = (step: EditableStepId) => {
    setStepErrors((prev) => {
      if (prev[step] === undefined) {
        return prev;
      }
      const next = { ...prev };
      delete next[step];
      return next;
    });
  };

  const clearOutcome = () => {
    setReviewError(null);
    setLastFailed(false);
  };

  const onSelectProduct = (id: number) => {
    if (id === productId) {
      return;
    }
    const next = offer?.products.find((p) => p.id === id) ?? null;
    setProductId(id);
    // A location stays chosen only if the new server has capacity there; the image always resets
    // (images are per server), and the new server's default is preselected once its list loads.
    setLocationCode((prev) => (prev !== null && next?.available_locations.includes(prev) ? prev : null));
    setImageCode(null);
    setStepErrors({});
    clearOutcome();
  };

  const onSelectLocation = (code: string) => {
    setLocationCode(code);
    clearStepError(STEP_IDS.location);
    clearOutcome();
  };

  const onSelectImage = (code: string) => {
    setImageCode(code);
    clearStepError(STEP_IDS.image);
    clearOutcome();
  };

  const onToggleKey = (id: number, checked: boolean) => {
    setKeyIds((prev) => {
      if (checked) {
        return prev.includes(id) || prev.length >= MAX_KEYS ? prev : [...prev, id];
      }
      return prev.filter((keyId) => keyId !== id);
    });
    clearStepError(STEP_IDS.keys);
    clearOutcome();
  };

  // --- Validity ---
  const serverValid = product !== null && product.available_locations.length > 0;
  const locationValid = product !== null && locationCode !== null && product.available_locations.includes(locationCode);
  const imageValid = selectedImage !== null;
  const keysValid = validKeyIds.length >= MIN_KEYS && validKeyIds.length <= MAX_KEYS;
  const stepsValid = serverValid && locationValid && imageValid && keysValid;

  const maxServers = offer?.max_servers ?? GPU_SERVER_MAX_ACTIVE;
  // Unknown servers (a failed read) count as none: the backend enforces the real cap.
  const activeCount = activeServerCount(servers ?? []);
  const blockers = startBlockers({
    isImpersonating,
    startsEnabled: offer?.starts.enabled === true,
    startsReason: offer?.starts.reason ?? null,
    activeCount,
    maxServers,
    stepsValid,
    acknowledged,
  });
  const canStart = blockers.length === 0;
  // While locked, only Retry (same key) is offered: it resends the locked body whatever the inputs.
  const canSubmit = session.locked ? !submitting : canStart && !submitting;

  const reserve = product !== null && servers !== null ? startReserve(product.first_hour_ttc, servers) : null;
  const shortfall = wallet !== null && reserve !== null ? isShortOf(wallet.balance, reserve) : null;

  const handleSubmit = useCallback(async (): Promise<{ jumpToStepId?: EditableStepId }> => {
    if (submittingRef.current) {
      return {};
    }
    let body: GpuServerStartBody;
    if (session.locked && session.lockedBody !== null) {
      body = session.lockedBody;
    } else {
      if (!canStart || product === null || locationCode === null || selectedImage === null) {
        return {};
      }
      body = {
        product_id: product.id,
        location_code: locationCode,
        image: selectedImage.code,
        pubkey_ids: validKeyIds,
        idempotency_key: session.key,
      };
    }
    const wasLocked = session.locked;

    submittingRef.current = true;
    setStepErrors({});
    setReviewError(null);
    setLastFailed(false);
    const outcome = await start(body);
    submittingRef.current = false;
    setSession((prev) => nextSession(prev, body, outcome, newIdempotencyKey));

    if (outcome.status === 'success') {
      const { server, replay } = outcome;
      if (server.status === 'failed') {
        // Refused by the provider and refunded: a new key (set above) lets the user try again.
        setLastFailed(true);
        addNotification({
          title: 'The server could not be created — its first hour is refunded to your balance',
          srTitle: 'Error alert',
          description: `${server.hostname} could not be created. You can try again.`,
          variant: 'danger',
        });
        setDrawerOpen(true);
        return {};
      }
      // Render the RETURNED server, never the inputs: after a replay they may differ.
      addNotification({
        title: replay ? `${server.hostname} was already started` : `${server.hostname} is starting`,
        srTitle: 'Success alert',
        description: replay
          ? 'This request had already started it — nothing was charged again.'
          : 'Its first hour was debited from your prepaid balance. It usually takes a few minutes, up to 30, to be ready.',
        variant: 'success',
      });
      setDrawerOpen(true);
      navigate(`${LIST_PATH}/${server.id}`);
      return {};
    }

    if (outcome.status === 'unconfirmed') {
      addNotification({
        title: 'We could not confirm the start',
        srTitle: 'Warning alert',
        description: outcome.message,
        variant: 'warning',
      });
      setDrawerOpen(true);
      return {};
    }

    const placement = placeStartError(outcome.message, outcome.fieldErrors, wasLocked);
    if (placement.step !== null) {
      setStepErrors({ [placement.step]: placement.message });
    } else {
      setReviewError({ message: placement.message, showTopUp: placement.showTopUp });
    }
    addNotification({
      title: 'Could not start the server',
      srTitle: 'Error alert',
      description: placement.message,
      variant: 'danger',
    });
    setDrawerOpen(true);
    return placement.step !== null ? { jumpToStepId: placement.step } : {};
  }, [
    session,
    canStart,
    product,
    locationCode,
    selectedImage,
    validKeyIds,
    start,
    addNotification,
    setDrawerOpen,
    navigate,
  ]);

  if (!auth.isAuthenticated) {
    return <Unauthenticated />;
  }

  const renderContent = () => {
    if (offer === null) {
      if (offerError !== null && !offerLoading) {
        return (
          <Alert
            variant="danger"
            isInline
            title="The GPU server offer could not be loaded"
            actionLinks={<AlertActionLink onClick={() => void refetchOffer()}>Retry</AlertActionLink>}
          >
            {offerError}
          </Alert>
        );
      }
      return <CenteredSpinner />;
    }

    return (
      <>
        {!offer.starts.enabled && (
          <Alert variant="info" isInline title="GPU servers cannot be started right now" className="pf-v6-u-mb-md">
            <p>{offer.starts.reason ?? 'GPU servers are not available at the moment'}</p>
            <p>You can still browse the servers and their prices.</p>
          </Alert>
        )}
        {offer.products.length === 0 ? (
          <EmptyState
            titleText="No GPU servers are on offer right now."
            icon={MicrochipIcon}
            variant="lg"
            headingLevel="h2"
          >
            <EmptyStateBody>Come back later to start a server.</EmptyStateBody>
            <EmptyStateFooter>
              <EmptyStateActions>
                <Button variant="secondary" component={(props) => <Link {...props} to={LIST_PATH} />}>
                  Back to GPU servers
                </Button>
              </EmptyStateActions>
            </EmptyStateFooter>
          </EmptyState>
        ) : (
          <Wizard navAriaLabel="GPU server start steps" onClose={() => navigate(LIST_PATH)} isVisitRequired>
            <WizardStep
              name="Server"
              id={STEP_IDS.server}
              isDisabled={session.locked}
              footer={{ isNextDisabled: !serverValid }}
            >
              <ServerStep
                products={offer.products}
                selectedProductId={productId}
                onSelectProduct={onSelectProduct}
                diskGb={offer.disk.size_gb}
                error={stepErrors[STEP_IDS.server] ?? null}
              />
            </WizardStep>
            <WizardStep
              name="Location"
              id={STEP_IDS.location}
              isDisabled={session.locked}
              footer={{ isNextDisabled: !locationValid }}
            >
              <LocationStep
                product={product}
                locations={offer.locations}
                selectedLocation={locationCode}
                onSelectLocation={onSelectLocation}
                error={stepErrors[STEP_IDS.location] ?? null}
              />
            </WizardStep>
            <WizardStep
              name="System image"
              id={STEP_IDS.image}
              isDisabled={session.locked}
              footer={{ isNextDisabled: !imageValid }}
            >
              <ImageStep
                productId={productId}
                images={images}
                loading={productId !== null && loadingFor(productId)}
                loadError={productId !== null ? errorFor(productId) : null}
                fetchImages={fetchImages}
                selectedImage={imageCode}
                onSelectImage={onSelectImage}
                error={stepErrors[STEP_IDS.image] ?? null}
              />
            </WizardStep>
            <WizardStep
              name="SSH keys"
              id={STEP_IDS.keys}
              isDisabled={session.locked}
              footer={{ isNextDisabled: !keysValid }}
            >
              <KeysStep
                pubkeys={pubkeys}
                loading={pubkeysLoading}
                loadError={pubkeysError}
                onReload={() => void refetchPubkeys()}
                selectedIds={validKeyIds}
                onToggleKey={onToggleKey}
                error={stepErrors[STEP_IDS.keys] ?? null}
              />
            </WizardStep>
            <WizardStep
              name="Review & start"
              id={STEP_IDS.review}
              footer={
                <ReviewFooter
                  primaryLabel={session.locked ? 'Retry (same key)' : lastFailed ? 'Try again' : 'Start server'}
                  canSubmit={canSubmit}
                  blocker={session.locked ? null : (blockers[0] ?? null)}
                  submitting={submitting}
                  locked={session.locked}
                  onSubmit={handleSubmit}
                />
              }
            >
              <ReviewStep
                product={product}
                locationCode={locationValid ? locationCode : null}
                locations={offer.locations}
                image={selectedImage}
                keys={selectedKeys}
                vatRate={offer.vat_rate}
                diskGb={offer.disk.size_gb}
                wallet={wallet}
                walletLoading={walletLoading}
                walletError={walletError}
                onRetryWallet={() => void refetchWallet()}
                serversError={servers === null ? serversError : null}
                onRetryServers={() => void refetchServers()}
                reserve={reserve}
                shortfall={shortfall}
                onTopUp={() => setIsTopUpOpen(true)}
                acknowledged={acknowledged}
                onAcknowledgedChange={setAcknowledged}
                locked={session.locked}
                reviewError={reviewError}
                lastFailed={lastFailed}
                activeCount={activeCount}
                maxServers={maxServers}
              />
            </WizardStep>
          </Wizard>
        )}
      </>
    );
  };

  return (
    <>
      <PageBreadcrumb hasBodyWrapper={false} hasShadowBottom={false}>
        <Breadcrumb ouiaId="GpuServerStartBreadcrumb">
          <BreadcrumbItem to="/datacenter">Datacenter</BreadcrumbItem>
          <BreadcrumbItem to={LIST_PATH}>GPU Servers</BreadcrumbItem>
          <BreadcrumbItem isActive>Start a server</BreadcrumbItem>
        </Breadcrumb>
      </PageBreadcrumb>
      <PageSection hasBodyWrapper variant={PageSectionVariants.default} isWidthLimited hasShadowBottom={true}>
        <Content component="h1">
          <Icon size="lg" style={{ marginRight: '8px' }}>
            <MicrochipIcon />
          </Icon>
          Start a GPU server
        </Content>
        <Content component="p">
          Choose a server, where it runs, its system image and your SSH keys. It is paid hour by hour from your prepaid
          balance.
        </Content>
      </PageSection>
      <PageSection hasBodyWrapper={false}>
        <GpuServerImpersonationAlert className="pf-v6-u-mb-md" />
        {renderContent()}
      </PageSection>

      {isTopUpOpen && (
        <TopUpModal
          isOpen
          onClose={() => setIsTopUpOpen(false)}
          onCredited={() => void refetchWallet()}
          wallet={wallet}
          defaultPaymentMethod={defaultPaymentMethod}
          walletLoading={walletLoading}
          walletError={walletError}
        />
      )}

      {/* The start calls the provider (5–35 s) and is never aborted. disableFocusTrap is load-bearing:
          this modal has no focusable element, and the focus trap throws with nothing to focus. */}
      <Modal variant="small" isOpen={submitting} aria-label="Starting your server" disableFocusTrap>
        <ModalHeader title="Starting your server" />
        <ModalBody>
          <Flex
            direction={{ default: 'column' }}
            alignItems={{ default: 'alignItemsCenter' }}
            spaceItems={{ default: 'spaceItemsMd' }}
          >
            <FlexItem>
              <Spinner size="xl" aria-label="Starting your server" />
            </FlexItem>
            <FlexItem>
              <Content component="p" className="pf-v6-u-text-align-center">
                Starting your server — this can take up to a minute. Don&apos;t close this page.
              </Content>
            </FlexItem>
          </Flex>
        </ModalBody>
      </Modal>
    </>
  );
};

export default StartGpuServer;
